/**
 * <velune-product-360> — lightweight drag/swipe product frame viewer.
 * No dependencies. Progressive enhancement: the first frame is server-rendered as a plain <img src>,
 * so the section works even if this script fails to load.
 *
 * Motion model (crisp, "pro viewer" feel):
 * - 1:1 tracking: while dragging, the rotation follows the pointer exactly, with no lag.
 * - Frames stay sharp: the next frame only cross-fades during a short window around each frame
 *   change (no permanent double image).
 * - Drawing happens once per display refresh (requestAnimationFrame → 60/120/144 Hz), only while
 *   something moves; frames are GPU layers and only opacity changes (no layout, no repaint).
 * - Short, precise inertia after a flick, always settling on a whole frame.
 */
class VeluneProduct360 extends HTMLElement {
  constructor() {
    super();
    this.frames = [];
    this.pos = 0; // rotation position in frames (float)
    this.velocity = 0; // frames per ms (inertia only)
    this.snapFrom = 0;
    this.snapTo = 0;
    this.snapStart = 0;
    this.snapping = false;
    this.dragging = false;
    this.loadedAll = false;
    this.raf = 0;
    this.lastTime = 0;
    this.samples = [];
    this.pointerX = 0;

    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.tick = this.tick.bind(this);
  }

  connectedCallback() {
    this.frames = Array.from(this.querySelectorAll('.velune-360__frame'));
    if (this.frames.length < 2) return;
    this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Each frame on its own compositor layer: changing opacity is then nearly free.
    this.frames.forEach((frame) => {
      frame.style.willChange = 'opacity';
      frame.style.transform = 'translateZ(0)';
      frame.style.transition = 'none';
    });
    this.render(0);

    this.addEventListener('pointerdown', this.onPointerDown);
    this.addEventListener('keydown', this.onKeyDown);
    this.setAttribute('tabindex', this.getAttribute('tabindex') || '0');

    if ('IntersectionObserver' in window) {
      this._io = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this._io.disconnect();
          this.preloadAll();
        },
        { rootMargin: '300px' }
      );
      this._io.observe(this);
    } else {
      this.preloadAll();
    }
  }

  disconnectedCallback() {
    if (this._io) this._io.disconnect();
    cancelAnimationFrame(this.raf);
  }

  get count() {
    return this.frames.length;
  }

  // Load every frame (data-src → src) and decode it up front, so a turn never waits on an image.
  preloadAll() {
    if (this.loadedAll) return this._loading || Promise.resolve();
    this.loadedAll = true;
    this._loading = Promise.all(
      this.frames.map((frame) => {
        frame.loading = 'eager';
        frame.decoding = 'async';
        const src = frame.getAttribute('data-src');
        if (src) {
          frame.setAttribute('src', src);
          frame.removeAttribute('data-src');
        }
        return frame.decode ? frame.decode().catch(() => {}) : Promise.resolve();
      })
    );
    return this._loading;
  }

  ready(frame) {
    return frame.complete && frame.naturalWidth > 0;
  }

  /*
    Paint position p. The current frame is fully opaque; the next one only fades in inside a short
    window at the end of each step (BLEND = 35 % of a step), with a smoothstep curve. Most of the
    time a single, sharp frame is visible; the change itself never "jumps".
  */
  render(p) {
    const n = this.count;
    const base = Math.floor(p);
    const frac = p - base;
    const a = ((base % n) + n) % n;
    const b = (a + 1) % n;
    const BLEND = 0.35;
    let mix = (frac - (1 - BLEND)) / BLEND;
    mix = mix <= 0 ? 0 : mix >= 1 ? 1 : mix * mix * (3 - 2 * mix);
    if (!this.ready(this.frames[b])) mix = 0;

    for (let i = 0; i < n; i++) {
      const frame = this.frames[i];
      const opacity = i === a ? 1 : i === b ? mix : 0;
      if (frame._o !== opacity) {
        frame.style.opacity = opacity;
        frame._o = opacity;
      }
      const z = i === b ? '2' : i === a ? '1' : '0';
      if (frame._z !== z) {
        frame.style.zIndex = z;
        frame._z = z;
      }
    }
    if (this._active !== a) {
      if (this._active != null && this.frames[this._active]) this.frames[this._active].classList.remove('is-active');
      this.frames[a].classList.add('is-active');
      this._active = a;
    }
  }

  start() {
    if (this.raf) return;
    this.lastTime = performance.now();
    this.raf = requestAnimationFrame(this.tick);
  }

  // One draw per display refresh, only while something moves.
  tick(now) {
    const dt = Math.min(40, now - this.lastTime || 16.7);
    this.lastTime = now;
    let moving = false;

    if (this.dragging) {
      // 1:1 with the pointer — no smoothing, no lag.
      this.pos = this.dragStartPos - (this.pointerX - this.dragStartX) / this.pxPerFrame();
      moving = true;
    } else if (this.velocity !== 0) {
      // Short, firm inertia.
      this.pos += this.velocity * dt;
      this.velocity *= Math.pow(0.988, dt);
      if (Math.abs(this.velocity) < 0.0015) {
        this.velocity = 0;
        this.beginSnap(now);
      }
      moving = true;
    } else if (this.snapping) {
      // Settle on the nearest whole frame (ease-out, ~180 ms).
      const t = Math.min(1, (now - this.snapStart) / 180);
      const e = 1 - Math.pow(1 - t, 3);
      this.pos = this.snapFrom + (this.snapTo - this.snapFrom) * e;
      if (t >= 1) {
        this.snapping = false;
        this.pos = this.snapTo;
      } else {
        moving = true;
      }
    }

    this.render(this.pos);
    if (moving) {
      this.raf = requestAnimationFrame(this.tick);
    } else {
      this.raf = 0;
    }
  }

  beginSnap(now) {
    this.snapFrom = this.pos;
    this.snapTo = Math.round(this.pos);
    this.snapStart = now || performance.now();
    this.snapping = this.snapTo !== this.snapFrom;
  }

  // Sensitivity: one full turn ≈ dragging across the product once (never faster than 18 px per frame).
  pxPerFrame() {
    if (!this._ppf) this._ppf = Math.max(18, this.clientWidth / this.count);
    return this._ppf;
  }

  onPointerDown(event) {
    if (this.count < 2) return;
    this.preloadAll();
    this._ppf = 0;
    this.dragging = true;
    this.snapping = false;
    this.velocity = 0;
    this.dragStartX = this.pointerX = event.clientX;
    this.dragStartPos = this.pos;
    this.samples = [{ x: event.clientX, t: event.timeStamp || performance.now() }];
    this.classList.add('is-dragging');

    if (this.setPointerCapture) {
      try {
        this.setPointerCapture(event.pointerId);
      } catch (e) {
        /* pointer capture is a progressive enhancement */
      }
    }

    this.addEventListener('pointermove', this.onPointerMove);
    this.addEventListener('pointerup', this.onPointerUp);
    this.addEventListener('pointercancel', this.onPointerUp);
    this.start();
  }

  onPointerMove(event) {
    if (!this.dragging) return;
    // Use coalesced events when available (high-rate mice/touch) for the most precise position.
    const events = event.getCoalescedEvents ? event.getCoalescedEvents() : null;
    const last = events && events.length ? events[events.length - 1] : event;
    this.pointerX = last.clientX;
    const t = event.timeStamp || performance.now();
    this.samples.push({ x: this.pointerX, t: t });
    while (this.samples.length > 2 && t - this.samples[0].t > 80) this.samples.shift();
    if (Math.abs(this.pointerX - this.dragStartX) > 3) event.preventDefault();
    this.start();
  }

  onPointerUp() {
    if (!this.dragging) return;
    this.dragging = false;
    this.classList.remove('is-dragging');
    this.removeEventListener('pointermove', this.onPointerMove);
    this.removeEventListener('pointerup', this.onPointerUp);
    this.removeEventListener('pointercancel', this.onPointerUp);

    const first = this.samples[0];
    const last = this.samples[this.samples.length - 1];
    const dt = last && first ? last.t - first.t : 0;
    const pxPerMs = dt > 0 ? (last.x - first.x) / dt : 0;
    // Only a real flick gets inertia; a slow release just settles on the nearest frame.
    if (!this.reduce && Math.abs(pxPerMs) > 0.25) {
      this.velocity = Math.max(-0.04, Math.min(0.04, -pxPerMs / this.pxPerFrame()));
    } else {
      this.velocity = 0;
      this.beginSnap();
    }
    this.start();
  }

  onKeyDown(event) {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    this.preloadAll();
    this.velocity = 0;
    this.snapFrom = this.pos;
    this.snapTo = Math.round(this.pos) + (event.key === 'ArrowRight' ? 1 : -1);
    this.snapStart = performance.now();
    if (this.reduce) {
      this.pos = this.snapTo;
      this.render(this.pos);
      return;
    }
    this.snapping = true;
    this.start();
  }
}

if (!customElements.get('velune-product-360')) {
  customElements.define('velune-product-360', VeluneProduct360);
}
